/*
 * consent_logic.ts is a collection of utilities to help with the handling of consents around Flow and related systems
 * it is in the common space which means it can be used in the frontend and backend, ensuring consistent logic through
 * the system.
 */
import { Consent, ConsentConfig, ConsentLevel } from "@flow/common/types";

/**
 * Build a Consent from a ConsentLevel and a ConsentConfig
 */
export function makeConsent(level: ConsentLevel, config: ConsentConfig): Consent {
  return {
    public_id: config.consent_text_public_id,
    consent_level: level,
    consent_method: config.input.type,
    consent_method_option: consentMethodOption(config, level),
  };
}

/*
 * Determine if a consent configuration has already been consented to or not
 *
 * @param already_consented_to is the list of consents a member has already seen and given an explicit or implicit response to.  This data comes from the CRM and is stored
 * on the session cookie.
 *
 * @param consent_text_public_id is from the consent configuration.
 *
 * @returns a boolean indicating if that consent configuration is in the list of consents the member has given an explicit response to.
 *
 * It is worth noting that a negative response to a consent will return true here.
 * i.e. if a member says do not contact me then we will not show them the consent again.
 * They have already been asked for the consent and told us no.
 */
export function isAlreadyConsentedTo(
  already_consented_to: Consent[] | undefined,
  consent_text_public_id: string
): boolean {
  if (already_consented_to === undefined) {
    return false;
  }

  return already_consented_to
    .map((consent) => {
      return consent.public_id;
    })
    .includes(consent_text_public_id);
}

/*
 * Determine if a consent configuration should be displayed to the member or not
 *
 * @param already_consented_to is the list of consents a member has already seen and given an explicit or implicit response to.  This data comes from the CRM and is stored
 * on the session cookie.
 *
 * @param config is the consent configuration.
 *
 * @returns a boolean indicating if that consent configuration should be displayed.
 *
 * Consents can be configured to always show, we would do this for the privacy policy for example.
 * Otherwise hide it if the member has already consented to it.
 */
export function showConsent(already_consented_to: Consent[] | undefined, config: ConsentConfig): boolean {
  return config.always_show || !isAlreadyConsentedTo(already_consented_to, config.consent_text_public_id);
}

/*
 * Determine what text a user consented to
 * @param consentConfig is the consent a user was consenting to
 * @param consentLevel is the level of consent the user has given
 * @returns the text label of the consent a user selected.
 * @returns undefined if the user did not have to pick an option
 */
export function consentMethodOption(consentConfig: ConsentConfig, consentLevel: ConsentLevel): string | undefined {
  switch (consentConfig.input.type) {
    // The 'implicit' method option defines a consent that is given implicitly by a user's actions of submittings a form.
    case "implicit":
      return undefined;
    // SW-200 - New 'onfieldpresent' method option.
    // The 'onfieldpresent' method option defines a consent that is controlled by the presence of a field in a form.
    // For example, onfieldpresent can be used to define a consent that is given when a user enters their email addres
    // into a field in a form.
    case "onfieldpresent":
      return undefined;
    // The 'checkbox' method option defines a consent that is controlled by the presence of a checkbox input in a form.
    case "checkbox":
      if (consentLevel === "explicit_opt_in") {
        return consentConfig.input.consent_label;
      } else {
        return undefined;
      }
    // The 'radiobuttons' method option defines a consent that is controlled by the presence of a radiobutton input in a form.
    case "radiobuttons":
      if (consentLevel === consentConfig.input.yes_consent_level) {
        return consentConfig.input.consent_yes_label;
      } else if (consentLevel === consentConfig.input.no_consent_level) {
        return consentConfig.input.consent_no_label;
      } else {
        return undefined;
      }
  }
}

/**
 * Updates a specific set of consents in an existing array of consents.  Used to update a list of consents
 * where some may have had consent_level, consent_method_option etc updated.
 * @param existingConsentSet - An array of Consent objects representing the current set of consents.
 * @param updatedConsents - An array of updated consents that will overrite or add to the existing set.
 * @returns A new array of Consent objects with the updated consents replaced in the original array.
 * public_id is used to uniquely identify Consents
 * It does not preserve order, but for consistency sorts by public_id
 */

export function updateConsents(existingConsentSet: Consent[], updatedConsents: Consent[]) {
  return [
    ...updatedConsents,
    ...existingConsentSet.filter(
      (consentA) => !updatedConsents.some((consentB) => consentA.public_id === consentB.public_id)
    ),
  ].sort((a, b) => a.public_id.localeCompare(b.public_id));
}
